import { get, merge, has } from 'lodash';
import {
  CHART_COLORS,
  VEGA_SPEC_AXIS_SETTINGS,
  VEGA_SPEC_LEGEND_SETTINGS,
} from 'constants/chart-spec';
import { CHART_CATEGORIES } from 'constants/chart-categories';
import { generateVegaLabelTextExpression } from 'utils/chart/vega-expressions';
import { generateVegaTooltip } from 'utils/chart/tooltips';
import {
  generateVegaSortParams,
  generateVegaTypeCoercionTransforms,
  generateVegaSortingAndLimitingTransforms,
} from 'utils/chart/transforms';

export default {
  name: '100% Stacked Area',
  displayName: '100%',
  order: 1,
  flavour: 'discrete',
  iconValue: 'area-stacked-100',
  category: CHART_CATEGORIES.AREA,
  channels: [
    {
      name: 'x',
      displayName: 'X Axis',
      required: true,
      channelType: 'category',
      intent: 'category-axis',
    },
    {
      name: 'y',
      displayName: 'Y Axis',
      required: true,
      channelType: 'aggregation',
      intent: 'value-axis',
      multipleSeries: true,
    },
    {
      name: 'color',
      displayName: 'Series',
      required: false,
      channelType: 'category',
      intent: 'series',
    },
  ],
  specFn: (encodings, customisations) => {
    const sortParams = generateVegaSortParams(encodings);
    const showDataMarkers = get(customisations.options, 'dataMarkers.enabled');
    return {
      autosize: { type: 'fit', contains: 'padding' },
      style: 'cell',
      data: [
        {
          name: 'transformedData',
          source: 'values',
          transform: [
            {
              type: 'impute',
              field: 'y',
              groupby: ['color'],
              key: 'x',
              method: 'value',
              value: 0,
            },
            ...generateVegaTypeCoercionTransforms('y'),
            ...generateVegaSortingAndLimitingTransforms(encodings),
            {
              type: 'stack',
              groupby: ['x'],
              field: 'y',
              sort: {
                field: [sortParams.color.field],
                order: ['descending'],
              },
              as: ['y_start', 'y_end'],
              offset: 'normalize',
            },
          ],
        },
      ],
      marks: [
        {
          type: 'group',
          from: {
            facet: {
              name: 'faceted_path_main',
              data: 'transformedData',
              groupby: ['color'],
            },
          },
          encode: {
            update: {
              width: { field: { group: 'width' } },
              height: { field: { group: 'height' } },
            },
          },
          marks: [
            {
              name: 'marks',
              type: 'area',
              style: ['area'],
              sort: sortParams.marks,
              from: { data: 'faceted_path_main' },
              encode: {
                update: {
                  orient: { value: 'vertical' },
                  x: { scale: 'x', field: 'x' },
                  y: { scale: 'y', field: 'y_end' },
                  y2: { scale: 'y', field: 'y_start' },
                  fill: { scale: 'color', field: 'color' },
                  tooltip: {
                    signal: generateVegaTooltip(encodings, customisations),
                  },
                },
              },
            },
          ],
          zindex: 1,
        },
        {
          type: 'group',
          from: {
            facet: {
              name: 'faceted_path_main',
              data: 'transformedData',
              groupby: ['color'],
            },
          },
          encode: {
            update: {
              width: { field: { group: 'width' } },
              height: { field: { group: 'height' } },
            },
          },
          marks: [
            {
              type: 'symbol',
              from: { data: 'faceted_path_main' },
              encode: {
                update: {
                  x: { scale: 'x', field: 'x' },
                  y: { scale: 'y', field: 'y_end' },
                  fill: showDataMarkers
                    ? { scale: 'color', field: 'color' }
                    : { value: 'transparent' },
                  stroke: {
                    scale: 'color',
                    field: 'color',
                  },
                  strokeWidth: { value: showDataMarkers ? 1.5 : 0 },
                  size: { value: 30 },
                },
              },
            },
          ],
          zindex: 2,
        },
        ...(!get(customisations.options, 'dataValueLabels.enabled')
          ? []
          : [
              {
                type: 'group',
                from: {
                  facet: {
                    name: 'faceted_path_main',
                    data: 'transformedData',
                    groupby: ['color'],
                  },
                },
                encode: {
                  update: {
                    width: { field: { group: 'width' } },
                    height: { field: { group: 'height' } },
                  },
                },
                marks: [
                  {
                    type: 'text',
                    name: 'valueLabels',
                    from: { data: 'faceted_path_main' },
                    encode: {
                      enter: {
                        text: {
                          signal: generateVegaLabelTextExpression(
                            encodings.y,
                            customisations.channels.y,
                            { field: 'y' }
                          ),
                        },
                        dy: { value: showDataMarkers ? -6 : -3 },
                        x: { scale: 'x', field: 'x' },
                        y: { scale: 'y', field: 'y_end' },
                        align: { value: 'center' },
                      },
                    },
                  },
                ],
                zindex: 2,
              },
            ]),
      ],
      scales: [
        {
          name: 'x',
          type: 'point',
          domain: { data: 'transformedData', field: 'x', sort: sortParams.x },
          range: [0, { signal: 'width' }],
          padding: 0.5,
        },
        {
          name: 'y',
          type: 'linear',
          domain: { data: 'transformedData', fields: ['y_start', 'y_end'] },
          range: [{ signal: 'height' }, 0],
          zero: true,
        },
        {
          name: 'color',
          type: 'ordinal',
          domain: {
            data: 'transformedData',
            field: 'color',
            sort: sortParams.color,
          },
          range:
            get(customisations.options, 'colorDiscrete.value') || CHART_COLORS,
        },
      ],
      axes: [
        merge({}, VEGA_SPEC_AXIS_SETTINGS, {
          scale: 'x',
          orient: 'bottom',
          grid: true,
          title: get(encodings, 'x.axis.title', ''),
          encode: {
            labels: {
              update: {
                angle: { value: 270 },
                align: { value: 'right' },
                baseline: { value: 'middle' },
                text: {
                  signal: generateVegaLabelTextExpression(
                    encodings.x,
                    customisations.channels.x
                  ),
                },
              },
            },
          },
          zindex: 1,
        }),
        merge({}, VEGA_SPEC_AXIS_SETTINGS, {
          scale: 'y',
          orient: 'left',
          grid: true,
          title: get(encodings, 'y.axis.title', ''),
          tickCount: { signal: 'ceil(height/40)' },
          format: '.0%',
          zindex: 1,
        }),
      ],
      legends: !has(encodings, 'color.legend.title')
        ? undefined
        : [
            merge({}, VEGA_SPEC_LEGEND_SETTINGS, {
              fill: 'color',
              title: get(encodings, 'color.legend.title'),
              encode: {
                labels: {
                  update: {
                    text: {
                      signal: generateVegaLabelTextExpression(
                        encodings.color,
                        customisations.channels.color
                      ),
                    },
                  },
                },
              },
            }),
          ],
    };
  },
};
